import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Chip } from "../ui-bits";
import { Card, STAGE_TONE, PRIORITY_LABEL, fmtDate, useBrandMembers } from "./common";

const Stat = ({ label, value, testId, tone }) => (
  <div className="card-dark p-5" data-testid={testId}><div className="label-caps mb-2">{label}</div><div className={`h-editorial text-3xl ${tone || ""}`}>{value}</div></div>
);

export function SamplingDashboard({ collectionId }) {
  const nav = useNavigate();
  const members = useBrandMembers();
  const [f, setF] = useState({ garment_id: "", responsible_id: "" });
  const [all, setAll] = useState([]);
  const [d, setD] = useState(null);
  const name = (id) => members.find(m => m.id === id)?.name || "—";
  useEffect(() => {
    api.get(`/sampling/collections/${collectionId}/dashboard`, { params: { garment_id: f.garment_id || undefined, responsible_id: f.responsible_id || undefined } })
      .then(r => { setD(r.data); if (!f.garment_id && !f.responsible_id) setAll(r.data.garments); }).catch(() => {});
  }, [collectionId, f]);
  if (!d) return <div className="text-sm opacity-60">Caricamento…</div>;
  const open = (id) => nav(`/garments/${id}/development`);
  return (
    <div data-testid="sampling-dashboard">
      <div className="flex flex-wrap gap-3 mb-6">
        <select className="input-dark !w-auto" value={f.garment_id} onChange={e => setF({ ...f, garment_id: e.target.value })} data-testid="sdash-filter-garment">
          <option value="">Tutti i capi</option>{all.map(g => <option key={g.id} value={g.id}>{g.code} — {g.name}</option>)}</select>
        <select className="input-dark !w-auto" value={f.responsible_id} onChange={e => setF({ ...f, responsible_id: e.target.value })} data-testid="sdash-filter-responsible">
          <option value="">Tutti i responsabili</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Capi" value={d.total} testId="sdash-total"/>
        <Stat label="Approvati / in produzione" value={d.completed} testId="sdash-completed" tone="text-green-700"/>
        <Stat label="Pronti per produzione" value={d.ready} testId="sdash-ready"/>
        <Stat label="Attività aperte" value={d.open_tasks_count} testId="sdash-open-tasks" tone={d.open_tasks_count ? "text-orange-600" : ""}/>
      </div>
      <Card title="Avanzamento campionario" testId="sdash-stages">
        <div className="flex h-3 w-full overflow-hidden mb-3" style={{ background: "var(--border)" }}>
          {d.by_stage.filter(s => s.count).map((s, i) => <div key={s.key} title={`${s.label}: ${s.count}`} style={{ width: `${100 * s.count / Math.max(1, d.total)}%`, opacity: 0.35 + 0.1 * i }} className="bg-[color:var(--text-primary)]"/>)}
        </div>
        <div className="flex flex-wrap gap-4 text-xs">{d.by_stage.map(s => <span key={s.key} data-testid={`sdash-stage-${s.key}`}>{s.label}: <b>{s.count}</b></span>)}</div>
      </Card>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Scadenze campioni (14 giorni)" testId="sdash-deadlines">
          {d.deadlines.length === 0 ? <div className="text-sm opacity-60">Nessuna scadenza imminente.</div> : d.deadlines.map(r => (
            <button key={r.id} onClick={() => open(r.id)} className="w-full flex justify-between text-sm py-2 border-b last:border-0 text-left" style={{ borderColor: "var(--border)" }}>
              <span>{r.code} — {r.name}</span><span className={r.overdue ? "text-red-600" : ""}>{fmtDate(r.sample_due_date)}{r.overdue ? " · in ritardo" : ""}</span></button>))}
        </Card>
        <Card title="Informazioni mancanti" testId="sdash-missing">
          {d.missing.length === 0 ? <div className="text-sm opacity-60">Nessuna informazione mancante.</div> : d.missing.map(m => (
            <div key={m.key} className="flex justify-between text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`sdash-missing-${m.key}`}>
              <span>{m.label}</span><span className="text-xs opacity-70">{m.count} capi · {m.garments.slice(0, 4).join(", ")}{m.garments.length > 4 ? "…" : ""}</span></div>))}
        </Card>
      </div>
      <Card title="Capi" testId="sdash-garments">
        <div className="overflow-x-auto"><table className="tbl w-full text-sm">
          <thead><tr><th>Capo</th><th>Fase</th><th>Responsabile</th><th>Scadenza</th><th className="text-right">Completo</th><th className="text-right">Mancanti</th></tr></thead>
          <tbody>{d.garments.map(r => (
            <tr key={r.id} className="cursor-pointer" onClick={() => open(r.id)} data-testid={`sdash-row-${r.id}`}>
              <td><b>{r.code}</b> {r.name}</td><td><Chip tone={STAGE_TONE[r.stage]}>{r.stage_label}</Chip></td><td>{name(r.responsible_id)}</td>
              <td className={r.overdue ? "text-red-600" : ""}>{fmtDate(r.sample_due_date)}</td><td className="text-right">{r.completion}%</td>
              <td className="text-right">{r.ready ? <Chip tone="green">Pronto</Chip> : `${r.missing}${r.missing_due ? ` (${r.missing_due} ora)` : ""}`}</td></tr>))}
          </tbody></table></div>
        {d.garments.length === 0 && <div className="text-sm opacity-60 py-4">Nessun capo in questa collezione.</div>}
      </Card>
      <Card title="Attività aperte" testId="sdash-tasks" action={<button onClick={() => nav("/tasks")} className="text-xs uppercase tracking-widest opacity-70 hover:opacity-100">Vai ad Attività</button>}>
        {d.open_tasks.length === 0 ? <div className="text-sm opacity-60">Nessuna attività aperta.</div> : d.open_tasks.map(t => (
          <div key={t.id} className="flex flex-wrap justify-between gap-2 text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
            <span>{t.title}{t.auto_key && <span className="text-xs opacity-60"> · automatico</span>}</span>
            <span className="text-xs opacity-70">{name(t.assignee_id)} · {fmtDate(t.start_date)} · {PRIORITY_LABEL[t.priority] || t.priority}</span></div>))}
      </Card>
    </div>
  );
}

export function SamplingSettingsCard() {
  const [checks, setChecks] = useState(null);
  const [msg, setMsg] = useState("");
  const [digestMsg, setDigestMsg] = useState("");
  useEffect(() => { api.get("/sampling/settings").then(r => setChecks(r.data.checks)).catch(() => {}); }, []);
  if (!checks) return null;
  const save = async () => {
    const r = await api.put("/sampling/settings", { required: checks.filter(c => c.required).map(c => c.key) });
    setChecks(r.data.checks); setMsg("Impostazioni salvate");
  };
  return (
    <div className="card-dark p-6 max-w-2xl" data-testid="sampling-settings">
      <div className="label-caps mb-2">Campionario · informazioni obbligatorie per la produzione</div>
      <p className="text-xs opacity-60 mb-4">Le voci attive generano promemoria dalla fase indicata e vanno completate prima di generare la scheda produzione.</p>
      {checks.map((c, i) => (
        <label key={c.key} className="flex items-center justify-between gap-3 py-2 border-b last:border-0 text-sm cursor-pointer" style={{ borderColor: "var(--border)" }}>
          <span className="flex items-center gap-2"><input type="checkbox" checked={c.required} onChange={e => { setMsg(""); setChecks(checks.map((x, j) => j === i ? { ...x, required: e.target.checked } : x)); }} data-testid={`sampling-req-${c.key}`}/> {c.label}</span>
          <span className="text-xs opacity-60">da {c.stage_label}</span>
        </label>
      ))}
      <button onClick={save} className="btn-gold mt-4" data-testid="sampling-settings-save">Salva</button>
      {msg && <span className="text-sm text-green-700 ml-3" data-testid="sampling-settings-msg">{msg}</span>}
      <div className="mt-6 pt-4 border-t text-sm" style={{ borderColor: "var(--border)" }}>
        <div className="label-caps mb-2">Riepilogo settimanale</div>
        <p className="text-xs opacity-60 mb-3">Ogni lunedì alle 8:00 tutto il team riceve nell'app l'elenco dei campioni in ritardo e delle informazioni mancanti.</p>
        <button onClick={async () => {
          setDigestMsg("");
          try { const r = await api.post("/sampling/digest/send-now"); setDigestMsg(r.data.sent ? "Riepilogo inviato nelle notifiche" : "Niente da segnalare: nessun riepilogo inviato"); }
          catch (e) { setDigestMsg(e.response?.status === 403 ? "Solo proprietario e admin possono inviarlo" : "Invio non riuscito"); }
        }} className="btn-ghost" data-testid="sampling-digest-send-now">Invia ora il riepilogo</button>
        {digestMsg && <span className="text-sm ml-3" data-testid="sampling-digest-msg">{digestMsg}</span>}
      </div>
    </div>
  );
}
